import React from 'react';
import { Table, Pagination, Button } from 'react-bootstrap';
import { FaCheckCircle, FaTimesCircle } from 'react-icons/fa';

const TAMANHO_PAGINA = 50;

/**
 * Wrapper de tabela com coluna Sel opcional, paginação server-side e callback de linha.
 * Props:
 *   dados: array de itens
 *   total: int total de registros
 *   pagina: int página atual
 *   colunas: [{key, label, render?, width?}]
 *   comSel: boolean — exibir coluna Sel
 *   titulosSel: Record<string, boolean>
 *   onSelToggle: (key) => void
 *   onMarcarTodos: () => void
 *   onDesmarcarTodos: () => void
 *   onLinhaClick: (item) => void
 *   onLinhaDblClick: (item) => void
 *   linhaSelecionada: qualquer valor para comparar com item
 *   chaveSelecao: campo do item para identificar linha selecionada e chave de Sel
 *   onPaginaChange: (pag) => void
 *   rodape: ReactNode botão extra
 */
const GridOcorrencias = ({
  dados,
  total,
  pagina,
  colunas,
  comSel,
  titulosSel,
  onSelToggle,
  onMarcarTodos,
  onDesmarcarTodos,
  onLinhaClick,
  onLinhaDblClick,
  linhaSelecionada,
  chaveSelecao,
  onPaginaChange,
  rodape,
}) => {
  const totalPaginas = Math.ceil((total || 0) / TAMANHO_PAGINA) || 1;

  const renderPaginacao = () => {
    if (totalPaginas <= 1 && (total || 0) <= TAMANHO_PAGINA) return null;
    const itens = [];
    const maxBotoes = 5;
    itens.push(
      <Pagination.First key="first" onClick={() => onPaginaChange(1)} disabled={pagina === 1} />
    );
    itens.push(
      <Pagination.Prev key="prev" onClick={() => onPaginaChange(pagina - 1)} disabled={pagina === 1} />
    );
    let inicio = Math.max(1, pagina - Math.floor(maxBotoes / 2));
    let fim = Math.min(totalPaginas, inicio + maxBotoes - 1);
    if (fim - inicio < maxBotoes - 1) inicio = Math.max(1, fim - maxBotoes + 1);
    if (inicio > 1) itens.push(<Pagination.Ellipsis key="e1" disabled />);
    for (let i = inicio; i <= fim; i++) {
      const n = i;
      itens.push(
        <Pagination.Item key={n} active={n === pagina} onClick={() => onPaginaChange(n)}>
          {n}
        </Pagination.Item>
      );
    }
    if (fim < totalPaginas) itens.push(<Pagination.Ellipsis key="e2" disabled />);
    itens.push(
      <Pagination.Next key="next" onClick={() => onPaginaChange(pagina + 1)} disabled={pagina >= totalPaginas} />
    );
    itens.push(
      <Pagination.Last key="last" onClick={() => onPaginaChange(totalPaginas)} disabled={pagina >= totalPaginas} />
    );
    return (
      <div className="d-flex justify-content-between align-items-center px-2 py-1" style={{ borderTop: '1px solid #dee2e6' }}>
        <small className="text-muted">
          Mostrando {(dados || []).length > 0 ? (pagina - 1) * TAMANHO_PAGINA + 1 : 0}
          {' até '}{Math.min(pagina * TAMANHO_PAGINA, total || 0)}{' de '}{total || 0}{' registros'}
        </small>
        <Pagination className="mb-0" size="sm">{itens}</Pagination>
      </div>
    );
  };

  return (
    <div>
      {comSel && (
        <div className="d-flex justify-content-end mb-1" style={{ gap: 6 }}>
          <Button size="sm" variant="outline-secondary" onClick={onMarcarTodos}>Marcar Todos</Button>
          <Button size="sm" variant="outline-secondary" onClick={onDesmarcarTodos}>Desmarcar Todos</Button>
        </div>
      )}
      <div style={{ overflowX: 'auto' }}>
        <Table striped bordered hover size="sm" style={{ fontSize: 13, marginBottom: 0 }}>
          <thead>
            <tr>
              {comSel && <th style={{ width: 50, textAlign: 'center' }}>Sel</th>}
              {(colunas || []).map((col) => (
                <th key={col.key} style={{ whiteSpace: 'nowrap', width: col.width || undefined }}>
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(dados || []).length === 0 ? (
              <tr>
                <td colSpan={(colunas || []).length + (comSel ? 1 : 0)} className="text-center text-muted py-3">
                  Nenhum registro
                </td>
              </tr>
            ) : (
              (dados || []).map((item, idx) => {
                const chave = chaveSelecao ? item[chaveSelecao] : idx;
                const selecionado = linhaSelecionada !== undefined && linhaSelecionada !== null && linhaSelecionada === chave;
                const sel = titulosSel && chave ? titulosSel[chave] : false;
                return (
                  <tr
                    key={`${chave}-${idx}`}
                    onClick={() => onLinhaClick && onLinhaClick(item)}
                    onDoubleClick={() => onLinhaDblClick && onLinhaDblClick(item)}
                    style={{
                      cursor: 'pointer',
                      background: selecionado ? '#cfe2ff' : undefined,
                    }}
                  >
                    {comSel && (
                      <td
                        style={{ textAlign: 'center', cursor: 'pointer' }}
                        onClick={(e) => { e.stopPropagation(); onSelToggle && onSelToggle(chave); }}
                      >
                        {sel
                          ? <FaCheckCircle style={{ color: '#28a745' }} />
                          : <FaTimesCircle style={{ color: '#dc3545' }} />}
                      </td>
                    )}
                    {(colunas || []).map((col) => (
                      <td key={col.key} style={{ whiteSpace: col.wrap ? undefined : 'nowrap' }}>
                        {col.render ? col.render(item) : (item[col.key] !== undefined && item[col.key] !== null ? String(item[col.key]) : '')}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      </div>
      {renderPaginacao()}
      {rodape && <div className="p-2">{rodape}</div>}
    </div>
  );
};

export default GridOcorrencias;
